import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import {
  EVENT_REASON_LABEL_KEYS,
  EVENT_TYPE_LABEL_KEYS,
  FAILURE_EVENT_TYPES,
} from "../../constants/payoutBatches.constants";
import type { PayoutBatchEvent } from "../../types/payoutBatches.types";
import { eventActorLabelKey } from "../../utils/eventActorLabel";

export interface BatchTimelineProps {
  events: PayoutBatchEvent[];
  currentUserId: string | null;
  isLoading: boolean;
  /** The newest event pulses while the batch is still moving. */
  isLive: boolean;
}

type Tone = "done" | "active" | "failed" | "neutral";

const SUCCESS_EVENT_TYPES = new Set(["APPROVED", "EXECUTION_FINISHED", "ITEM_PAID", "VALIDATION_FINISHED"]);

const toneFor = (event: PayoutBatchEvent, isNewest: boolean, isLive: boolean): Tone => {
  if (FAILURE_EVENT_TYPES.has(event.event_type)) return "failed";
  if (isNewest && isLive) return "active";
  if (SUCCESS_EVENT_TYPES.has(event.event_type)) return "done";
  return "neutral";
};

const readString = (metadata: Record<string, unknown>, key: string): string | null => {
  const value = metadata[key];
  return typeof value === "string" && value ? value : typeof value === "number" ? String(value) : null;
};

/**
 * The batch's trail as a vertical timeline: what happened, who did it, when — plus the code that explains a
 * failure.
 */
export function BatchTimeline({ events, currentUserId, isLoading, isLive }: BatchTimelineProps) {
  const { t, activeLanguage } = useTranslations("payoutBatches");

  if (isLoading) {
    return (
      <div className="space-y-2" aria-hidden="true">
        <ColocaSkeleton className="h-4 w-2/3" />
        <ColocaSkeleton className="h-4 w-1/2" />
        <ColocaSkeleton className="h-4 w-3/5" />
      </div>
    );
  }

  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("events_empty")}</p>;
  }

  return (
    <ol className="space-y-0">
      {events.map((event, index) => {
        const tone = toneFor(event, index === 0, isLive);
        const labelKey = EVENT_TYPE_LABEL_KEYS[event.event_type];
        const code = readString(event.metadata, "code") ?? readString(event.metadata, "halt_code");
        const run = readString(event.metadata, "run_no");
        const rawReason = readString(event.metadata, "reason");
        const reasonKey = rawReason ? EVENT_REASON_LABEL_KEYS[rawReason] : undefined;
        const reason = reasonKey ? t(reasonKey) : rawReason;
        return (
          <li key={event.id} className="relative flex gap-3 pb-4 last:pb-0">
            {index < events.length - 1 && (
              <span aria-hidden="true" className="absolute top-4 left-[3.5px] h-full w-px bg-border" />
            )}
            <span
              aria-hidden="true"
              className={cn(
                "mt-1.5 size-2 shrink-0 rounded-full",
                tone === "done" && "bg-success",
                tone === "active" && "animate-pulse bg-primary",
                tone === "failed" && "bg-destructive",
                tone === "neutral" && "bg-muted-foreground/60",
              )}
            />
            <div className="min-w-0 space-y-0.5">
              <p className="text-sm font-medium text-foreground">
                {labelKey ? t(labelKey) : event.event_type}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(eventActorLabelKey(event, currentUserId))}
                {run ? ` · ${t("event_meta_run", { run })}` : ""}
              </p>
              {code && (
                <p className="font-mono text-[11px] text-muted-foreground">
                  {t("event_meta_code")}: {code}
                </p>
              )}
              {reason && (
                <p className="text-xs text-muted-foreground">
                  {t("event_meta_reason")}: {reason}
                </p>
              )}
              <p className="font-mono text-[11px] text-muted-foreground tabular-nums">
                {formatDateTime(event.created_at, {
                  locale: activeLanguage,
                  includeTime: true,
                })}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
